import React from "react";
import ReactAudioPlayer from "react-audio-player";
import styled from "styled-components";
import IconButton from "@mui/material/IconButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import PlaybackRateSelector from "./PlaybackRateSelector";
import { useEffect, useState, useRef } from "react";

const FakePDF = ({ dialoguesData, onPlayChange }) => {
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    onPlayChange(isPlaying);
  }, [isPlaying, onPlayChange]);

  useEffect(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.audioEl.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const playAudio = async () => {
    if (audioPlayerRef.current) {
      try {
        await audioPlayerRef.current.audioEl.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error("Audio play failed:", error);
      }
    }
  };

  const pauseAudio = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.audioEl.current.pause();
      setIsPlaying(false);
    }
  };

  const playAudioForLine = (audioFile, index) => {
    setCurrentFileIndex(index);
    setCurrentAudio(audioFile);
    if (audioPlayerRef.current) {
      const audioElement = audioPlayerRef.current.audioEl.current;

      const setRateAndPlay = () => {
        audioElement.playbackRate = playbackRate;
        audioElement.play();
        audioElement.removeEventListener("canplay", setRateAndPlay);
      };

      audioElement.addEventListener("canplay", setRateAndPlay);
    }
  };

  const playNextAudio = () => {
    if (
      currentFileIndex !== null &&
      currentFileIndex < dialoguesData.dialogues.length - 1
    ) {
      playAudioForLine(
        dialoguesData.dialogues[currentFileIndex + 1].audio,
        currentFileIndex + 1
      );
    } else {
      setIsPlaying(false);
    }
  };

  const playAllAudioSequentially = () => {
    if (isPlaying) {
      setIsPlaying(false);
      pauseAudio();
    } else if (currentFileIndex === null) {
      setIsPlaying(true);
      playAudioForLine(dialoguesData.dialogues[0].audio, 0);
    } else {
      setIsPlaying(true);
      playAudio();
    }
  };

  const restartAudio = () => {
    setCurrentFileIndex(0);
    setCurrentAudio(dialoguesData.dialogues[0].audio);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.audioEl.current.currentTime = 0;
    }
  };

  return (
    <PDFContainer>
      <ScriptHeader>
        <h3 style={headerStyle}>{dialoguesData.header.title}</h3>
        <ButtonContainer>
          <IconButton onClick={playAllAudioSequentially}>
            {isPlaying ? (
              <PauseIcon style={iconStyles} />
            ) : (
              <PlayArrowIcon style={iconStyles} />
            )}
          </IconButton>
          <IconButton onClick={restartAudio}>
            <ReplayIcon style={iconStyles} />
          </IconButton>
          <PlaybackRateSelector
            currentRate={playbackRate}
            onChange={(newRate) => setPlaybackRate(newRate)}
            iconStyles={iconStyles}
          />
        </ButtonContainer>
      </ScriptHeader>
      <ScriptContent>
        {dialoguesData.dialogues.map((dialogue, index) => (
          <Dialogue
            key={index}
            onClick={() => playAudioForLine(dialogue.audio, index)}
            className={currentFileIndex === index ? "highlighted" : ""}
          >
            <p className="character-name">{dialogue.character}</p>
            <p>{dialogue.text}</p>
          </Dialogue>
        ))}
      </ScriptContent>
      <ReactAudioPlayer
        ref={audioPlayerRef}
        src={currentAudio}
        autoPlay
        onEnded={playNextAudio}
      />
    </PDFContainer>
  );
};

const PDFContainer = styled.div`
  padding-top: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5); // Adjusted shadow for more lift
  border-radius: 10px;
  padding: 20px;
  margin: 50px auto;
  width: 90%;
  max-width: 1000px;
`;

const ScriptContent = styled.div`
  background: white; // Changed to white background
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.18); // Adjusted border color to be visible on white background
  padding: 20px;
  width: 75%;
  display: flex;
  flex-direction: column;
  font-family: "Courier New", Courier, monospace;
  color: black; // Changed text color to black
  font-size: 20px;
`;

const Dialogue = styled.div`
  color: black;
  padding: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s, color 0.3s;
  cursor: pointer;

  &.highlighted {
    background-color: #f3f3f3;
    color: #000; // Dark black color
    font-weight: bold; // Making the text bold
  }

  &:hover {
    color: #1e90ff;
  }
`;

const ButtonContainer = styled.div`
  display: flex; // New container to hold the buttons and align them horizontally
`;

const ScriptHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
  font-size: 40px;
  font-family: "Courier New", Courier, monospace;
  font-weight: bold;
  display: flex;
  flex-direction: column; // Keep it as column to maintain the button position below the text
  align-items: center;
`;

const iconStyles = {
  color: "#EBE1C2",
  fontSize: "50px",
};

const headerStyle = {
  marginBottom: "8px",
  color: "#EBE1C2",
};

export default FakePDF;
